import React from "react";

const PHASE_ORDER_EN = ["init", "ai_1a", "ai_1b", "ai_2", "validator", "sia", "render"];
const PHASE_ORDER_AR = ["init", "ai_1a", "ai_1b", "ai_2", "validator", "sia", "translate", "render"];
const PHASE_LABELS_EN = {
  init:      "Initialising",
  ai_1a:     "Writing environmental section",
  ai_1b:     "Writing social section",
  ai_2:      "Writing governance & appendix",
  validator: "Validating data consistency",
  sia:       "Building Sustainability in Action table",
  translate: "Translating to Arabic",
  render:    "Rendering 60+ page PDF",
  // Back-compat: older in-flight reports may still report "ai_1" (single phase).
  // Map it to the env-section step so the bar doesn't appear stuck.
  ai_1:      "Writing environmental section",
};
const PHASE_LABELS_AR = {
  init:      "التهيئة",
  ai_1a:     "كتابة القسم البيئي",
  ai_1b:     "كتابة القسم الاجتماعي",
  ai_2:      "كتابة الحوكمة والملحق",
  validator: "التحقّق من اتساق البيانات",
  sia:       "بناء جدول الاستدامة في الممارسة",
  translate: "الترجمة إلى العربية",
  render:    "توليد ملف PDF بأكثر من 60 صفحة",
  ai_1:      "كتابة القسم البيئي",
};

// Shared phased-progress card. Used both during initial Generate (ReportBuilder)
// and during Regenerate-prose (ReportView). Pure presentational, caller wires
// up the phase polling via api.pollUntilGenerated().
export default function GenerationProgress({ phase, elapsed, language, statusText }) {
  const isAr = language === "ar";
  const order = isAr ? PHASE_ORDER_AR : PHASE_ORDER_EN;
  const PHASE_LABELS = isAr ? PHASE_LABELS_AR : PHASE_LABELS_EN;
  // Resolve phase index, older reports may use "ai_1" (now split into 1a/1b)
  const resolvePhase = (p) => (p === "ai_1" ? "ai_1a" : p);

  // Track highest phase index reached so the visual never regresses if
  // a backend phase update arrives out of order.
  const [maxIdx, setMaxIdx] = React.useState(0);
  React.useEffect(() => {
    const i = order.indexOf(resolvePhase(phase));
    if (i > maxIdx) setMaxIdx(i);
    if (phase === "done") setMaxIdx(order.length);
  }, [phase, order, maxIdx]);

  const currentIdx = Math.max(order.indexOf(resolvePhase(phase)), maxIdx);
  const mins = Math.floor(elapsed / 60);
  const secs = String(elapsed % 60).padStart(2, "0");
  const etaText = isAr ? "12–20 دقيقة" : "12–20 min";
  const pct = Math.min(100, Math.round(((currentIdx + (phase === "done" ? 0 : 0.5)) / order.length) * 100));
  const footer = isAr
    ? "يمكنك ترك هذه العلامة مفتوحة بأمان، سيستمر الإنشاء على الخادم حتى إن غادرت الصفحة، وسيكون التقرير بانتظارك في لوحة التحكم عند الانتهاء."
    : "You can safely leave this tab open, generation continues on the server even if you navigate away, and the report will be waiting in your dashboard when it's done.";

  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-6" data-testid="generation-progress">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="font-['Outfit'] text-[10px] font-bold tracking-[0.14em] uppercase text-[#01AF9B] mb-1">{isAr ? "جارٍ الإنشاء بواسطة الذكاء الاصطناعي" : "AI Generation in progress"}</div>
          <div className="font-['Outfit'] text-2xl font-bold text-white tabular-nums" data-testid="gen-elapsed">{mins}:{secs}</div>
          <div className="text-xs text-slate-500 mt-0.5">{isAr ? `منقضٍ · المدة المعتادة ${etaText}` : `elapsed · typical run ${etaText}`}</div>
        </div>
        <div className="w-14 h-14 relative">
          <svg viewBox="0 0 40 40" className="w-14 h-14 -rotate-90">
            <circle cx="20" cy="20" r="16" fill="none" stroke="#1e293b" strokeWidth="3" />
            <circle cx="20" cy="20" r="16" fill="none" stroke="#01AF9B" strokeWidth="3"
              strokeDasharray={`${(pct / 100) * 100.5} 100.5`} strokeLinecap="round"
              className="transition-all duration-700" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-['Outfit'] text-[11px] font-bold text-white tabular-nums">{pct}%</div>
        </div>
      </div>

      <ol className="space-y-2.5">
        {order.map((p, idx) => {
          const done = idx < currentIdx || phase === "done";
          const active = idx === currentIdx && phase !== "done";
          return (
            <li key={p}
              data-testid={`gen-phase-${p}`}
              data-state={done ? "done" : active ? "active" : "pending"}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-all ${
                active ? "border-[#01AF9B]/40 bg-[#01AF9B]/5" :
                done ? "border-slate-800 bg-[#0B0C10]" :
                "border-slate-800/40 bg-transparent"
              }`}>
              <span className={`flex-none w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                done ? "bg-[#01AF9B] text-[#070709]" :
                active ? "bg-[#01AF9B]/20 text-[#01AF9B]" :
                "bg-slate-800 text-slate-600"
              }`}>
                {done ? "✓" : active ? (
                  <span className="w-3 h-3 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
                ) : (idx + 1)}
              </span>
              <span className={`font-['Outfit'] text-sm flex-1 ${
                done ? "text-slate-400 line-through decoration-slate-700" :
                active ? "text-white font-semibold" :
                "text-slate-600"
              }`}>
                {PHASE_LABELS[p] || p}
              </span>
              {active && (
                <span className="text-[10px] font-['Outfit'] font-bold tracking-wider text-[#01AF9B] uppercase">{isAr ? "قيد التقدم" : "In progress"}</span>
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-5 pt-4 border-t border-slate-800 flex items-start gap-2">
        <span className="text-[#01AF9B] text-sm">✦</span>
        <p className="text-xs text-slate-500 leading-relaxed">
          {statusText && phase !== "done"
            ? <><span className="text-slate-300">{statusText}</span> {footer}</>
            : footer}
        </p>
      </div>
    </div>
  );
}
